/**
 * Global Error Handler Utility
 * Provides centralized error handling and logging
 * 
 * Requirements: 19.4, 36.2
 */

/**
 * Log error to console with context
 * @param {Error} error - The error object
 * @param {string} context - Context where the error occurred
 * @param {object} additionalInfo - Additional information about the error
 */
export function logError(error, context = "Unknown", additionalInfo = {}) {
  console.error(`[Error Handler] ${context}:`, {
    message: error.message,
    stack: error.stack,
    ...additionalInfo,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Get user-friendly error message from error object
 * @param {Error} error - The error object
 * @param {string} defaultMessage - Default message if error is not recognized
 * @returns {string} User-friendly error message
 */
export function getUserFriendlyMessage(error, defaultMessage = "An unexpected error occurred") {
  if (!error) return defaultMessage;

  // Network errors
  if (error.message?.includes("fetch") || error.message?.includes("network")) {
    return "Network error. Please check your internet connection.";
  }

  // Authentication errors
  if (error.message?.includes("unauthorized") || error.message?.includes("401")) {
    return "Authentication failed. Please log in again.";
  }

  // Permission errors
  if (error.message?.includes("forbidden") || error.message?.includes("403")) {
    return "You don't have permission to perform this action.";
  }

  // Database errors
  if (error.message?.includes("database") || error.message?.includes("PouchDB")) {
    return "Database error. Please try again.";
  }

  // Sync errors
  if (error.message?.includes("sync") || error.message?.includes("replication")) {
    return "Sync error. Your changes are saved locally and will sync when connection is restored.";
  }

  // Validation errors
  if (error.message?.includes("validation") || error.message?.includes("invalid")) {
    return error.message; // Validation messages are usually user-friendly
  }

  // Timeout errors
  if (error.message?.includes("timeout")) {
    return "Request timed out. Please try again.";
  }

  // Server errors
  if (error.message?.includes("500") || error.message?.includes("server error")) {
    return "Server error. Please try again later.";
  }

  // Default: use error message if it's short and readable, otherwise use default
  if (error.message && error.message.length < 100 && !error.message.includes("Error:")) {
    return error.message;
  }

  return defaultMessage;
}

/**
 * Handle error with logging and optional toast notification
 * @param {Error} error - The error object
 * @param {string} context - Context where the error occurred
 * @param {Function} showToast - Optional toast function to show error message
 * @param {object} options - Additional options
 * @param {string} options.userMessage - Custom user-friendly message
 * @param {boolean} options.silent - If true, don't show toast (only log)
 */
export function handleError(error, context, showToast = null, options = {}) {
  // Log error to console
  logError(error, context, options.additionalInfo);

  // Show toast notification if provided and not silent
  if (showToast && !options.silent) {
    const message = options.userMessage || getUserFriendlyMessage(error);
    showToast("error", message);
  }
}

/**
 * Wrap async function with error handling
 * @param {Function} fn - Async function to wrap
 * @param {string} context - Context for error logging
 * @param {Function} showToast - Optional toast function
 * @returns {Function} Wrapped function
 */
export function withErrorHandling(fn, context, showToast = null) {
  return async (...args) => {
    try {
      return await fn(...args);
    } catch (error) {
      handleError(error, context, showToast);
      throw error; // Re-throw so caller can handle if needed
    }
  };
}

/**
 * Create error handler for specific operation
 * @param {string} operation - Operation name (e.g., "Create Invoice")
 * @param {Function} showToast - Toast function
 * @returns {Function} Error handler function
 */
export function createOperationErrorHandler(operation, showToast) {
  return (error) => {
    handleError(error, operation, showToast, {
      userMessage: `Failed to ${operation.toLowerCase()}. ${getUserFriendlyMessage(error)}`,
    });
  };
}

export default {
  logError,
  getUserFriendlyMessage,
  handleError,
  withErrorHandling,
  createOperationErrorHandler,
};
